import { Dataset, v4Dataset } from 'hooks/data/datasets/types';
import { ActivityType } from 'hooks/data/search/types';

import { isSameRelease } from './datasetRef';
import { CompareDataset, DatasetRef } from './types';

type ReleaseFallback = { ver: string; system_model: string };

const releaseLabel = ({ version, systemModel }: DatasetRef) => `${version}-${systemModel}`;

export const compareEventProps = (datasets: (CompareDataset | undefined)[]) => {
  const resolved = datasets.filter((dataset): dataset is CompareDataset => Boolean(dataset));
  const ids = resolved.map((dataset) => dataset.id);
  const canDescribeShape = resolved.length > 1 && resolved.length === datasets.length;

  const isSame = (pick: (dataset: CompareDataset) => string) =>
    resolved.every((dataset) => pick(dataset) === pick(resolved[0]));

  return {
    dataset_ids: ids,
    dataset_count: ids.length,
    dataset_releases: resolved.map(releaseLabel),
    is_cross_release: resolved.length > 1 && !resolved.every((dataset) => isSameRelease(dataset, resolved[0])),
    ...(canDescribeShape && {
      same_activity: isSame((dataset) => dataset.activityName),
      same_reference_product: isSame((dataset) => dataset.referenceProduct),
      same_geography: isSame((dataset) => dataset.geography),
    }),
  };
};

// Search results only send the "Long name (CODE)" label, so the code is read back out of it.
export const geographyCode = (geography: string) => geography.match(/\(([^()]+)\)$/)?.[1] ?? geography;

export const geographyAndRelease = (dataset?: CompareDataset, ref?: DatasetRef) =>
  [dataset && geographyCode(dataset.geography), ref && releaseLabel(ref)].filter(Boolean).join(' · ');

const toActivityType = (type?: string | null): ActivityType | undefined =>
  Object.values(ActivityType).find((known) => known === type);

/** `fallback` must be the release the dataset was requested from, not the route's. */
export const datasetToCompareItem = (id: string, data: Dataset, { ver, system_model }: ReleaseFallback): CompareDataset => ({
  id,
  version: data.version || ver,
  systemModel: data.system_model || system_model,
  activityName: data.activity_name,
  referenceProduct: data.reference_product,
  geography: data.geography.short_name
    ? `${data.geography.long_name} (${data.geography.short_name})`
    : data.geography.long_name,
  unit: data.unit,
  sector: data.sector,
  activityType: toActivityType(data.activity_type),
  hasAccess: data.has_access,
});

export const v4DatasetToCompareItem = (
  id: string,
  data: v4Dataset,
  { ver, system_model }: ReleaseFallback
): CompareDataset => ({
  id,
  version: data.version || ver,
  systemModel: data.system_model || system_model,
  activityName: data.name,
  referenceProduct: data.product,
  geography: data.geography_short ? `${data.geography} (${data.geography_short})` : data.geography,
  unit: data.unit,
  hasAccess: data.has_access,
});
